/**
 * Os erros do Cognito em pt-BR, por ação da tela de login. O texto do serviço
 * (em inglês, às vezes com o e-mail ou o código de volta) nunca chega à tela:
 * só o código do erro decide a frase.
 */

/** A política de senha do pool (`password_policy` em infra/portal/main.tf). */
export const PASSWORD_MIN_LENGTH = 12;

export const PASSWORD_HINT = `Mínimo de ${PASSWORD_MIN_LENGTH} caracteres, com letra maiúscula, minúscula, número e símbolo.`;

/**
 * A primeira exigência da política que a senha não cumpre, ou null. Só adianta
 * o aviso antes da ida ao Cognito — quem decide é ele.
 */
export function passwordPolicyError(password: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `A senha precisa ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres.`;
  }
  if (!/[a-z]/.test(password)) return 'A senha precisa de uma letra minúscula.';
  if (!/[A-Z]/.test(password)) return 'A senha precisa de uma letra maiúscula.';
  if (!/[0-9]/.test(password)) return 'A senha precisa de um número.';
  if (!/[^A-Za-z0-9]/.test(password)) return 'A senha precisa de um símbolo.';
  return null;
}

export type AuthAction =
  | 'signIn'
  | 'signUp'
  | 'confirmSignUp'
  | 'resendCode'
  | 'requestReset'
  | 'confirmReset'
  | 'confirmLink'
  | 'setPassword'
  | 'updateProfile';

/** Um erro de autenticação já traduzido; `code` é o do Cognito, para a tela decidir o próximo passo. */
export class PortalAuthError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'PortalAuthError';
    this.code = code;
  }
}

export const UNCONFIRMED_ACCOUNT = 'UserNotConfirmedException';

/** O equivalente do Supabase Auth para a mesma condição. */
export const SUPABASE_UNCONFIRMED_ACCOUNT = 'email_not_confirmed';

/** True quando o login parou porque o e-mail ainda não foi confirmado — a tela oferece reenviar o código. */
export function isUnconfirmedAccount(err: unknown): boolean {
  return (
    err instanceof PortalAuthError &&
    (err.code === UNCONFIRMED_ACCOUNT || err.code === SUPABASE_UNCONFIRMED_ACCOUNT)
  );
}

const FALLBACK: Record<AuthAction, string> = {
  signIn: 'Não foi possível entrar. Tente novamente.',
  signUp: 'Não foi possível criar a conta. Tente novamente.',
  confirmSignUp: 'Não foi possível confirmar o código. Tente novamente.',
  resendCode: 'Não foi possível reenviar o código. Tente novamente.',
  requestReset: 'Não foi possível enviar o código. Tente novamente.',
  confirmReset: 'Não foi possível redefinir a senha. Tente novamente.',
  confirmLink: 'Não foi possível confirmar o link. Peça um novo e-mail.',
  setPassword: 'Não foi possível salvar a senha. Tente novamente.',
  updateProfile: 'Não foi possível salvar o nome. Tente novamente.',
};

const WRONG_CREDENTIALS = 'E-mail ou senha incorretos.';
const INVALID_CODE = 'Código inválido. Confira o código enviado por e-mail.';
const TOO_MANY = 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';

export function toPortalAuthError(err: unknown, action: AuthAction): PortalAuthError {
  if (err instanceof PortalAuthError) return err;
  const code = errorCode(err);
  return new PortalAuthError(code || 'Unknown', messageFor(code, errorText(err), action));
}

function messageFor(code: string, text: string, action: AuthAction): string {
  switch (code) {
    case 'NotAuthorizedException':
      // O mesmo código cobre senha errada, conta desativada, excesso de
      // tentativas e "já confirmada": só o texto do serviço os separa.
      if (/attempts exceeded/i.test(text)) return TOO_MANY;
      if (/disabled/i.test(text)) return 'Esta conta está desativada.';
      if (action === 'confirmSignUp' && /CONFIRMED/.test(text)) {
        return 'Este e-mail já está confirmado. Entre com sua senha.';
      }
      return action === 'signIn' ? WRONG_CREDENTIALS : FALLBACK[action];
    case 'UserNotFoundException':
      // O pool não diz quais e-mails existem (prevent_user_existence_errors):
      // a tela também não — "inexistente" e "senha errada" têm a mesma frase.
      if (action === 'signIn') return WRONG_CREDENTIALS;
      if (action === 'confirmSignUp' || action === 'confirmReset') return INVALID_CODE;
      return FALLBACK[action];
    case UNCONFIRMED_ACCOUNT:
      return 'Seu e-mail ainda não foi confirmado. Use o código que enviamos ou peça um novo.';
    case 'UsernameExistsException':
    case 'AliasExistsException':
      return 'Já existe uma conta com este e-mail. Entre com sua senha ou recupere o acesso.';
    case 'InvalidPasswordException':
      return `A senha não atende à política de segurança. ${PASSWORD_HINT}`;
    case 'CodeMismatchException':
      return INVALID_CODE;
    case 'ExpiredCodeException':
      return 'O código expirou. Peça um novo código.';
    case 'CodeDeliveryFailureException':
      return 'Não foi possível enviar o código por e-mail. Tente novamente mais tarde.';
    case 'InvalidParameterException':
      return action === 'confirmReset'
        ? `A nova senha não atende à política de segurança. ${PASSWORD_HINT}`
        : 'Confira os dados informados e tente novamente.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return TOO_MANY;
    case 'NetworkError':
      return 'Não foi possível alcançar o servidor de autenticação. Verifique sua conexão.';
    default:
      return FALLBACK[action];
  }
}

/** O discriminador do erro do SDK (`code`, senão `name`). */
function errorCode(err: unknown): string {
  if (typeof err !== 'object' || err === null) return '';
  const { code, name } = err as { code?: unknown; name?: unknown };
  if (typeof code === 'string' && code) return code;
  if (typeof name === 'string' && name && name !== 'Error') return name;
  return '';
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : '';
}
